import React, { useState, useEffect } from 'react';
import { 
    Button, 
    Container, 
    Typography, 
    Paper,
    Box,
    Chip,
    Stack,
    Divider,
    List,
    ListItem,
    ListItemText,
    Rating,
    TextField,
    Alert
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { userRequest } from '../../requestMethods';
import EditIcon from '@mui/icons-material/Edit';

const GrammarTopicViewer = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    
    const [topic, setTopic] = useState({
        slug: '',
        title: '',
        summary: '',
        content: '',
        examples: [],
        tags: [],
        language: null,
        level: null
    });
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Review state
    const [rating, setRating] = useState(0);
    const [reviewComment, setReviewComment] = useState('');
    const [reviewSubmitted, setReviewSubmitted] = useState(false);
    const [reviewError, setReviewError] = useState(null);

    useEffect(() => {
        fetchTopicBySlug();
    }, [slug]);

    const fetchTopicBySlug = async () => {
        setLoading(true);
        try {
            // Set Accept header to get JSON instead of HTML
            const response = await userRequest.get(`/grammar/topic/${slug}`, {
                headers: {
                    'Accept': 'application/json'
                }
            });
            setTopic(response.data);
        } catch (err) {
            console.error('Error fetching grammar topic by slug:', err);
            setError('Failed to load grammar topic. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitReview = async () => {
        if (rating === 0) {
            setReviewError('Please select a rating');
            return;
        }
        
        setReviewError(null);
        
        try {
            await userRequest.post(`/grammar/${topic._id}/review`, {
                rating,
                comment: reviewComment
            });
            setReviewSubmitted(true);
            // Reset form
            setRating(0);
            setReviewComment('');
        } catch (err) {
            console.error('Error submitting review:', err);
            setReviewError('Failed to submit review. Please try again.');
        }
    };

    if (loading) {
        return (
            <Container maxWidth="lg">
                <Typography variant="h6" sx={{ mt: 4 }}>Loading topic...</Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg">
                <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>
                <Box sx={{ mt: 2 }}>
                    <Button variant="outlined" onClick={() => navigate('/grammar')}>
                        Back to Topics
                    </Button>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 3 }}>
                    {topic.title}
                </Typography>
                <Button 
                    variant="contained" 
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/grammar/edit/${topic._id}`)}
                >
                    Edit
                </Button>
            </Box>

            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                {/* Language and Level Info */}
                {(topic.language || topic.level) && (
                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        {topic.language && (
                            <Chip 
                                label={`Language: ${topic.language.name}`} 
                                variant="outlined" 
                                color="primary"
                            />
                        )}
                        {topic.level && (
                            <Chip 
                                label={`Level: ${topic.level.name}`} 
                                variant="outlined" 
                                color="secondary"
                            />
                        )}
                    </Box>
                )}

                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Summary
                </Typography>
                <Typography paragraph sx={{ mb: 3 }}>
                    {topic.summary}
                </Typography>

                <Divider sx={{ mb: 3 }} />

                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                    Content
                </Typography>
                <Box 
                    sx={{ mb: 3 }}
                    dangerouslySetInnerHTML={{ __html: topic.content }}
                />

                {topic.examples && topic.examples.length > 0 && topic.examples[0] !== '' && (
                    <>
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                            Examples
                        </Typography>
                        <List>
                            {topic.examples.map((example, index) => (
                                <ListItem key={index} sx={{ pl: 0 }}>
                                    {/* <ListItemText primary={`${index + 1}. ${example}`} /> */}
                                    <div dangerouslySetInnerHTML={{ __html: example }} />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}

                {topic.tags && topic.tags.length > 0 && (
                    <>
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                            Tags
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                            {topic.tags.map((tag, index) => (
                                <Chip
                                    key={index}
                                    label={tag}
                                    variant="outlined"
                                />
                            ))}
                        </Stack>
                    </>
                )}
            </Paper>
            
            {/* Review Section */}
            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                <Typography variant="h5" gutterBottom>
                    Review this Topic
                </Typography>
                
                {reviewSubmitted ? (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Thank you for your review!
                    </Alert>
                ) : (
                    <>
                        {reviewError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {reviewError}
                            </Alert>
                        )}
                        
                        <Box sx={{ mb: 3 }}>
                            <Typography component="legend">Rating</Typography>
                            <Rating
                                name="topic-rating"
                                value={rating}
                                onChange={(event, newValue) => {
                                    setRating(newValue);
                                }}
                                size="large"
                            />
                        </Box>
                        
                        <TextField
                            label="Comments (optional)"
                            multiline
                            rows={4}
                            value={reviewComment}
                            onChange={(e) => setReviewComment(e.target.value)}
                            fullWidth
                            sx={{ mb: 3 }}
                        />
                        
                        <Button 
                            variant="contained" 
                            onClick={handleSubmitReview}
                        >
                            Submit Review
                        </Button>
                    </>
                )}
            </Paper>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 4 }}>
                <Button 
                    variant="outlined" 
                    onClick={() => navigate('/grammar')}
                >
                    Back to Topics
                </Button>
            </Box>
            
            <div style={{ height: '60px' }}></div>
        </Container>
    );
};

export default GrammarTopicViewer; 